import React from "react";
import { Link, useNavigate } from "react-router-dom";
import Seo from "../components/Seo.jsx";

/* How it works — standalone explainer page.
   Mounted at /how-it-works as its own route (no app shell, no sidebar).
   Leads with the product walkthrough video, then walks through the path
   from a product description to a ranked, ready-to-act shortlist. Shares
   the editorial chrome (.expl-*) with /why-it-works. */

const COMPANY_NAME = "Community Ninja";
const WALKTHROUGH_SRC =
  "https://communityninja.s3.us-east-2.amazonaws.com/blue_walk.mp4";

const HOW_JSONLD = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "How Community Ninja works",
  description:
    "From a short product description to a ranked daily brief: how Community Ninja reads Reddit for intent, scores every match, and hands you an actionable shortlist.",
  step: [
    { "@type": "HowToStep", name: "Teach it your signal" },
    { "@type": "HowToStep", name: "It maps the intent behind the words" },
    { "@type": "HowToStep", name: "It reads conversations as they happen" },
    { "@type": "HowToStep", name: "It scores, ranks, and filters" },
    { "@type": "HowToStep", name: "You act on a ready-made shortlist" },
  ],
};

/* The pipeline, told as a numbered editorial run. High level on purpose:
   what happens and why it matters, without spelling out the internals. */
const SECTIONS = [
  {
    n: "01",
    title: "You teach it your signal",
    lede:
      "You describe what you do, who you help, and the moments that show up right before someone goes looking. A few honest lines are enough to begin.",
  },
  {
    n: "02",
    title: "It maps the intent behind the words",
    lede:
      "Community Ninja translates your product into the language real people use when they actually have the problem you solve. It learns the questions, complaints, and comparisons that mean someone is in motion, not just curious.",
  },
  {
    n: "03",
    title: "It reads conversations as they happen",
    lede:
      "On a schedule and on demand, it works through fresh posts and comments across the communities that matter to you, reading each one for meaning instead of matching a string of text.",
  },
  {
    n: "04",
    title: "It scores, ranks, and filters",
    lede:
      "Every candidate is weighed on intent, relevance, context, freshness, and quality. Weak and off-topic threads are dropped before they ever reach you, so what remains is short and worth reading.",
  },
  {
    n: "05",
    title: "You act on a ready-made shortlist",
    lede:
      "What lands is a ranked list, each thread carrying a fit score, an intent headline, and a plain-English reason. You open it already knowing why it matters and where you can add value.",
  },
];

function HowItWorksScreen() {
  const navigate = useNavigate();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="expl-page screen-enter">
      <Seo
        title="How it works"
        description="From a short product description to a ranked daily brief: how Community Ninja reads Reddit for intent, scores every match, and hands you an actionable shortlist."
        keywords={[
          "how community ninja works",
          "reddit intent analysis",
          "buyer intent detection",
          "reddit monitoring workflow",
          "customer discovery",
        ]}
        jsonLd={HOW_JSONLD}
      />

      <header className="legal-top">
        <button
          type="button"
          className="legal-back"
          onClick={() => {
            if (window.history.length > 1) navigate(-1);
            else navigate("/");
          }}
          aria-label="Go back"
        >
          <window.I.ArrowLeft size={14} />
          <span>Back</span>
        </button>
        <Link to="/" className="legal-brand" aria-label={`${COMPANY_NAME} home`}>
          <span className="legal-brand-name">{COMPANY_NAME}</span>
        </Link>
      </header>

      <article className="expl-shell">
        {/* ---------- Hero ---------- */}
        <div className="expl-hero">
          <div className="expl-eyebrow">
            <span className="expl-eyebrow-dot" />
            <span>How it works</span>
          </div>
          <h1 className="expl-title">
            Deep intent analysis, turned into an{" "}
            <span className="wv4-serif">actionable shortlist.</span>
          </h1>
          <p className="expl-lede">
            Here is everything that happens between you describing your product
            and a short, ranked brief landing in front of you. No keyword soup,
            no firehose to wade through. Just the conversations that genuinely
            fit, in the order they deserve your attention.
          </p>
        </div>

        {/* ---------- Walkthrough video ---------- */}
        <figure className="expl-video">
          <video
            className="expl-video-el"
            src={WALKTHROUGH_SRC}
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            aria-label="Community Ninja product walkthrough"
          />
        </figure>
        <figcaption className="expl-video-cap">
          Deep intent analysis into an actionable shortlist
        </figcaption>

        {/* ---------- Body ---------- */}
        <div className="expl-body">
          {SECTIONS.map((s) => (
            <section className="expl-section" key={s.n}>
              <div className="expl-section-num">{s.n}</div>
              <div className="expl-section-content">
                <h2 className="expl-section-title">{s.title}</h2>
                <p className="expl-section-lede">{s.lede}</p>
              </div>
            </section>
          ))}
        </div>

        {/* ---------- Closing ---------- */}
        <section className="expl-result">
          <span className="expl-result-eyebrow">The payoff</span>
          <h2 className="expl-result-title">
            Open Reddit already knowing where to{" "}
            <span className="wv4-serif">spend your time.</span>
          </h2>
          <p className="expl-result-sub">
            A focused daily brief instead of an endless scroll. The deep work
            runs quietly in the background. You just decide and engage.
          </p>
          <Link to="/" className="btn btn-primary btn-lg expl-result-cta">
            <window.I.Sparkles size={15} />
            Build my radar
          </Link>
        </section>
      </article>

      {window.LegalFooter && <window.LegalFooter variant="page" />}
    </div>
  );
}

window.HowItWorksScreen = HowItWorksScreen;

export default HowItWorksScreen;
